import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import {
  ShoppingCartIcon, XMarkIcon, PlusIcon, MinusIcon,
  ArrowRightOnRectangleIcon, CheckCircleIcon, ClockIcon, TruckIcon, Bars3Icon,
} from '@heroicons/react/24/solid';
import { storeAPI, getStoreByDomain, paymentsAPI } from '../../services/api';

const THEMES: Record<string, { primary: string; primaryDark: string; bg: string; hero: string; cardBorder: string; text: string }> = {
  sage:  { primary: '#059669', primaryDark: '#047857', bg: '#f0fdf4', hero: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)', cardBorder: '#bbf7d0', text: '#1e293b' },
  wheat: { primary: '#d97706', primaryDark: '#b45309', bg: '#fffbeb', hero: 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #b45309 100%)', cardBorder: '#fde68a', text: '#1e293b' },
  stone: { primary: '#475569', primaryDark: '#334155', bg: '#f8fafc', hero: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', cardBorder: '#cbd5e1', text: '#1e293b' },
  earth: { primary: '#92400e', primaryDark: '#78350f', bg: '#fdf4e7', hero: 'linear-gradient(135deg, #451a03 0%, #78350f 50%, #92400e 100%)', cardBorder: '#fbbf24', text: '#1e293b' },
  cream: { primary: '#1e40af', primaryDark: '#1e3a8a', bg: '#eff6ff', hero: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)', cardBorder: '#bfdbfe', text: '#1e293b' },
};

function buildCustomTheme(ct: any): typeof THEMES['sage'] {
  const primary    = ct?.primary     || '#059669';
  const primaryDark = ct?.primaryDark || '#047857';
  const bg         = ct?.bg          || '#f0fdf4';
  const cardBorder = ct?.cardBorder  || '#bbf7d0';
  // Build hero gradient by darkening primaryDark
  const hero = `linear-gradient(135deg, ${darkenHex(primaryDark, 20)} 0%, ${darkenHex(primaryDark, 8)} 50%, ${primaryDark} 100%)`;
  return { primary, primaryDark, bg, hero, cardBorder, text: '#1e293b' };
}

function darkenHex(hex: string, pct: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const num = parseInt(clean, 16);
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * pct));
  const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(2.55 * pct));
  const b = Math.max(0, (num & 0xff) - Math.round(2.55 * pct));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

const CATEGORY_EMOJI: Record<string, string> = {
  bread: '🍞', loaf: '🍞', sourdough: '🥖', baguette: '🥖', roll: '🥐',
  croissant: '🥐', pastry: '🥐', cake: '🎂', muffin: '🧁', cookie: '🍪',
  bagel: '🥯', pretzel: '🥨', scone: '🫓', default: '🍞',
};

function getProductEmoji(name: string, category: string): string {
  const hay = (name + ' ' + category).toLowerCase();
  for (const [key, emoji] of Object.entries(CATEGORY_EMOJI)) {
    if (hay.includes(key)) return emoji;
  }
  return '🍞';
}

const STATUS_COLOR: Record<string, string> = {
  pending: '#f59e0b', confirmed: '#3b82f6', baking: '#8b5cf6',
  ready: '#10b981', delivered: '#059669', cancelled: '#ef4444',
};

interface Product {
  id: number; name: string; description: string;
  price_per_unit: string; unit: string; stock_quantity: number;
  image?: string;
  allergens?: string;
  ingredients?: string;
  category?: { name: string };
}
interface CartItem { product: Product; quantity: number; }
interface Member { id: number; email: string; full_name: string; first_name?: string; last_name?: string; phone?: string; address?: string; }

function getSubdomain(): string {
  const parts = window.location.hostname.split('.');
  if (parts.length >= 3) return parts[0];
  return 'demo';
}

function isCustomDomain(): boolean {
  const host = window.location.hostname;
  return !host.endsWith('.sourdoughsy.com') && host !== 'sourdoughsy.com' && host !== 'localhost';
}

const STORAGE_KEY = (s: string) => `retail_token_${s}`;
const MEMBER_KEY  = (s: string) => `retail_member_${s}`;
const CART_KEY    = (s: string) => `retail_cart_${s}`;

export default function PublicStore() {
  const rawSubdomain = getSubdomain();
  const onCustomDomain = isCustomDomain();
  // subdomain may be resolved later from store data when on a custom domain
  const [resolvedSubdomain, setResolvedSubdomain] = useState(onCustomDomain ? '' : rawSubdomain);
  const api = useMemo(() => storeAPI(resolvedSubdomain), [resolvedSubdomain]);

  const [store, setStore]       = useState<any>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [theme, setTheme]       = useState(THEMES.sage);

  const [cart, setCart]         = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_KEY(onCustomDomain ? '' : rawSubdomain));
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [cartAnim, setCartAnim] = useState(false);

  const [member, setMember] = useState<Member | null>(() => {
    try { return JSON.parse(localStorage.getItem(MEMBER_KEY(resolvedSubdomain)) || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY(resolvedSubdomain)) || '');

  const [authModal, setAuthModal] = useState<'login' | 'register' | null>(null);
  const [authForm, setAuthForm]   = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [checkoutModal, setCheckoutModal] = useState(false);
  const [checkoutForm, setCheckoutForm]   = useState({ notes: '', delivery_address: '', phone: '', bake_date: '', tip_amount: 0, coupon_code: '' });
  const [fulfillmentChoice, setFulfillmentChoice] = useState<'delivery' | 'pickup'>('delivery');
  const [tipPct, setTipPct] = useState<number | 'custom'>(0);
  const [tipCustom, setTipCustom] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState<{ code: string; discount_amount: string; discount_type: string; discount_value: string } | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [orderSuccess, setOrderSuccess]       = useState<any>(null);

  const [myOrders, setMyOrders]   = useState<any[]>([]);
  const [ordersModal, setOrdersModal] = useState(false);

  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Pages
  const [pages, setPages] = useState<any[]>([]);
  const [activePage, setActivePage] = useState<string | null>(null);  // null = home

  // Mobile menu
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    if (!resolvedSubdomain) return;
    try { localStorage.setItem(CART_KEY(resolvedSubdomain), JSON.stringify(cart)); } catch {}
  }, [cart, resolvedSubdomain]);

  // On custom domains resolvedSubdomain starts as '' — re-hydrate cart once we know it
  useEffect(() => {
    if (!onCustomDomain || !resolvedSubdomain) return;
    try {
      const saved = localStorage.getItem(CART_KEY(resolvedSubdomain));
      if (saved) setCart(JSON.parse(saved));
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSubdomain]);

  // Contact form
  const [contactModal, setContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [contactError, setContactError] = useState('');

  const load = useCallback(async () => {
    try {
      let res;
      if (onCustomDomain) {
        // Fetch by custom domain; backend resolves the company
        res = await getStoreByDomain(window.location.hostname);
        // Store the real subdomain so subsequent API calls work
        const username = res.data?.store?.company_username || res.data?.store?.company_name;
        if (username) setResolvedSubdomain(username);
      } else {
        res = await api.getStore();
      }
      const d = res.data;
      if (!d.active) {
        setStore(d.store || null);
        setError('This store is not active yet.');
        setLoading(false);
        return;
      }
      setStore(d.store);
      setProducts(d.products || []);
      const ct = d.store?.color_theme;
      setTheme(ct === 'custom' ? buildCustomTheme(d.store?.custom_theme) : (THEMES[ct] || THEMES.sage));
      // Load pages
      try {
        const pRes = await api.getPages();
        setPages(Array.isArray(pRes.data) ? pRes.data : []);
      } catch {}
    } catch {
      setError('Store not found.');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Apply meta tags + GA when store loads
  useEffect(() => {
    if (!store) return;
    const name = store.store_name || store.company_name || resolvedSubdomain || 'Our Bakery';
    // Page title
    document.title = store.meta_title || name;
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = store.meta_description || '';
    // Google Analytics
    if (store.ga_tracking_id && !document.getElementById('ga-script')) {
      const s1 = document.createElement('script');
      s1.id = 'ga-script';
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${store.ga_tracking_id}`;
      document.head.appendChild(s1);
      const s2 = document.createElement('script');
      s2.id = 'ga-inline';
      s2.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${store.ga_tracking_id}');`;
      document.head.appendChild(s2);
    }
  }, [store, resolvedSubdomain]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const orderNum = params.get('order') || '';
      setOrderSuccess({ order_number: orderNum, _stripe_paid: true });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const ex = prev.find(c => c.product.id === product.id);
      if (ex) return prev.map(c => c.product.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { product, quantity: 1 }];
    });
    setCartAnim(true);
    setTimeout(() => setCartAnim(false), 400);
  };

  const updateQty = (id: number, delta: number) =>
    setCart(prev => prev.map(c => c.product.id === id ? { ...c, quantity: Math.max(1, c.quantity + delta) } : c));

  const removeFromCart = (id: number) =>
    setCart(prev => prev.filter(c => c.product.id !== id));

  const cartSubtotal = cart.reduce((s, c) => s + parseFloat(c.product.price_per_unit) * c.quantity, 0);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);
  const couponDiscount = couponApplied ? parseFloat(couponApplied.discount_amount) : 0;
  const discountedSubtotal = Math.max(0, cartSubtotal - couponDiscount);
  const storeDeliveryFee = parseFloat(store?.delivery_fee || 0);
  // Pickup/delivery logic
  const isPickupOnly = store?.fulfillment_mode === 'pickup';
  const isBothMode = store?.fulfillment_mode === 'both' || !store?.fulfillment_mode;
  // When mode is 'both', use the customer's fulfillmentChoice; otherwise derive from mode
  const customerChosePickup = isBothMode ? fulfillmentChoice === 'pickup' : isPickupOnly;
  const deliveryFeeApplied = !customerChosePickup && storeDeliveryFee > 0 ? storeDeliveryFee : 0;
  // Fee computation (mirrors backend _compute_order_fees)
  const baseForFees = discountedSubtotal + deliveryFeeApplied;
  const taxAmount = baseForFees * (parseFloat(store?.tax_rate || 0)) / 100;
  const platformFeePct = parseFloat(store?.platform_fee_pct || 0);
  const platformFeeCustomer = baseForFees * platformFeePct / 100 * (store?.platform_fee_customer_pct || 0) / 100;
  const ccBase = baseForFees + taxAmount + platformFeeCustomer;
  const ccFeeCustomer = store?.cc_fee_customer_pct > 0
    ? (ccBase * 2.9 / 100 + 0.30) * (store.cc_fee_customer_pct || 0) / 100
    : 0;
  const cartBeforeTip = baseForFees + taxAmount + platformFeeCustomer + ccFeeCustomer;
  const cartTotal = cartBeforeTip + checkoutForm.tip_amount;

  const categories = useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => p.category?.name && cats.add(p.category.name));
    return Array.from(cats);
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter(p => p.category?.name === activeCategory);
  }, [products, activeCategory]);

  const handleAuth = async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      if (authModal === 'login') {
        const res = await api.login(authForm.email, authForm.password);
        const { token: t, member: m } = res.data;
        localStorage.setItem(STORAGE_KEY(resolvedSubdomain), t);
        localStorage.setItem(MEMBER_KEY(resolvedSubdomain), JSON.stringify(m));
        setToken(t); setMember(m); setAuthModal(null);
      } else {
        await api.register(authForm);
        setAuthModal('login');
        setAuthError('Account created! Please log in.');
      }
    } catch (e: any) {
      setAuthError(e?.response?.data?.error || 'An error occurred');
    }
    setAuthLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY(resolvedSubdomain));
    localStorage.removeItem(MEMBER_KEY(resolvedSubdomain));
    setToken(''); setMember(null);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;

    // ── Validation ──────────────────────────────────────────
    const hasBakeDates = store?.bake_schedule_type === 'exact_dates'
      ? (store?.bake_exact_dates || []).length > 0
      : (store?.bake_days || []).length > 0;
    if (hasBakeDates && !checkoutForm.bake_date) {
      setCheckoutError('Please select a bake date.');
      return;
    }
    if (!customerChosePickup && store?.fulfillment_mode === 'delivery' && !checkoutForm.delivery_address) {
      setCheckoutError('Please enter your delivery address.');
      return;
    }
    if (!member && !authForm.phone && !checkoutForm.phone) {
      setCheckoutError('Please enter your phone number.');
      return;
    }
    setCheckoutError('');
    // ────────────────────────────────────────────────────────

    setCheckoutLoading(true);

    const onCustomDomain = window.location.hostname !== 'sourdoughsy.com' &&
      !window.location.hostname.endsWith('.sourdoughsy.com') &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1';

    // If store accepts online payments → redirect to Stripe Checkout
    if (store?.accepts_online_payment) {
      try {
        const orderData = {
          items: cart.map(c => ({ product_id: c.product.id, quantity: c.quantity })),
          ...checkoutForm,
          guest_email: member ? '' : authForm.email,
          guest_name: member ? '' : `${authForm.first_name} ${authForm.last_name}`.trim(),
          guest_phone: member ? '' : (authForm.phone || checkoutForm.phone),
          member_token: token || '',
          return_origin: window.location.origin,
          ...(onCustomDomain ? { domain: window.location.hostname } : {}),
        };
        let res;
        if (onCustomDomain) {
          res = await paymentsAPI.createRetailCheckoutByDomain(orderData);
        } else {
          res = await paymentsAPI.createRetailCheckout(resolvedSubdomain, orderData);
        }
        // Redirect to Stripe-hosted checkout page
        window.location.href = res.data.checkout_url;
        return; // don't setCheckoutLoading(false) — page is navigating away
      } catch (e: any) {
        alert(e?.response?.data?.error || 'Failed to start payment. Please try again.');
        setCheckoutLoading(false);
        return;
      }
    }

    // No Stripe → free order (no payment)
    try {
      const res = await api.placeOrder({
        items: cart.map(c => ({ product_id: c.product.id, quantity: c.quantity })),
        ...checkoutForm,
        guest_email: member ? '' : authForm.email,
        guest_name: member ? '' : `${authForm.first_name} ${authForm.last_name}`.trim(),
        guest_phone: member ? '' : (authForm.phone || checkoutForm.phone),
      }, token || undefined);
      setOrderSuccess(res.data);
      setCart([]);
      setCheckoutModal(false);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to place order');
    }
    setCheckoutLoading(false);
  };

  const loadMyOrders = async () => {
    if (!token) return;
    try {
      const res = await api.getOrders(token);
      setMyOrders(Array.isArray(res.data) ? res.data : []);
    } catch {}
    setOrdersModal(true);
  };

  // ── Profile ────────────────────────────────────────────────
  const [profileModal, setProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', phone: '', address: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  const openProfileModal = () => {
    setProfileForm({
      first_name: member?.first_name || member?.full_name?.split(' ')[0] || '',
      last_name: member?.last_name || member?.full_name?.split(' ').slice(1).join(' ') || '',
      phone: member?.phone || '',
      address: member?.address || '',
    });
    setProfileError('');
    setProfileModal(true);
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError('');
    try {
      const res = await api.updateProfile(profileForm, token);
      const updated = res.data;
      setMember(updated);
      localStorage.setItem(MEMBER_KEY(resolvedSubdomain), JSON.stringify(updated));
      setProfileModal(false);
    } catch (e: any) {
      setProfileError(e?.response?.data?.error || 'Failed to save changes');
    }
    setProfileSaving(false);
  };

  // Pre-fill delivery address from saved member profile
  const openCheckout = () => {
    setTipPct(0);
    setTipCustom('');
    setCouponInput('');
    setCouponApplied(null);
    setCouponError('');
    // Default fulfillment choice for 'both' mode
    const mode = store?.fulfillment_mode || 'both';
    if (mode === 'both') setFulfillmentChoice('delivery');
    setCheckoutForm(p => ({
      ...p,
      delivery_address: p.delivery_address || member?.address || '',
      phone: p.phone || member?.phone || '',
      tip_amount: 0,
      coupon_code: '',
    }));
    setCheckoutModal(true);
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await api.applyCoupon(couponInput.trim().toUpperCase(), cartSubtotal);
      setCouponApplied(res.data);
      setCheckoutForm(p => ({ ...p, coupon_code: res.data.code }));
    } catch (e: any) {
      setCouponError(e?.response?.data?.error || 'Invalid coupon code');
      setCouponApplied(null);
      setCheckoutForm(p => ({ ...p, coupon_code: '' }));
    }
    setCouponLoading(false);
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError('');
    setCheckoutForm(p => ({ ...p, coupon_code: '' }));
  };

  const T = theme;

  // ── Hero layout helpers ────────────────────────────────────
  function heroElStyle(pos: string): React.CSSProperties {
    const [vert, horiz] = (pos || 'middle-center').split('-');
    const transforms: string[] = [];
    const style: React.CSSProperties = { position: 'absolute', zIndex: 2 };
    if (vert === 'top')    style.top = '28px';
    else if (vert === 'middle') { style.top = '50%'; transforms.push('translateY(-50%)'); }
    else                  style.bottom = '28px';
    if (horiz === 'left')   { style.left = '28px'; style.textAlign = 'left'; }
    else if (horiz === 'center') { style.left = '50%'; transforms.push('translateX(-50%)'); style.textAlign = 'center'; }
    else                  { style.right = '28px'; style.textAlign = 'right'; }
    if (transforms.length) style.transform = transforms.join(' ');
    return style;
  }

  const LOGO_SIZES: Record<string, number> = { small: 60, medium: 96, large: 136, xl: 180, xxl: 240 };
  const LOGO_RADII: Record<string, string> = { none: '0', circle: '50%', rounded: '18px', square: '8px' };
  const LOGO_BORDERS: Record<string, string> = {
    none: 'none',
    thin: '2px solid rgba(255,255,255,0.65)',
    medium: '3px solid rgba(255,255,255,0.88)',
    thick: '5px solid white',
  };

  const logoSize   = LOGO_SIZES[store?.logo_size   || 'medium'];
  const logoRadius = LOGO_RADII[store?.logo_shape  || 'circle'];
  const logoBorder = LOGO_BORDERS[store?.logo_border || 'medium'];

  // ── Schedule badge helpers ─────────────────────────────────
  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const SCHEDULE_FONT: Record<string, string> = { small: '0.7rem', medium: '0.85rem', large: '1rem' };
  function scheduleLabel(arr: number[]): string {
    if (!arr || arr.length === 0) return '';
    return arr.slice().sort((a, b) => a - b).map(d => DAY_NAMES[d] || '').filter(Boolean).join(', ');
  }

  // Returns upcoming valid bake dates for recurring-days schedule.
  // bakeDays/lastOrderDays use 0=Mon … 6=Sun indexing.
  function getValidBakeDates(
    bakeDays: number[],
    lastOrderDays: number[],
    lastOrderTime: string,
  ): string[] {
    if (!bakeDays || bakeDays.length === 0) return [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dates: string[] = [];

    for (let i = 1; i <= 84 && dates.length < 20; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);

      // JS getDay(): 0=Sun … 6=Sat  →  our idx: 0=Mon … 6=Sun
      const ourIdx = (d.getDay() + 6) % 7;
      if (!bakeDays.includes(ourIdx)) continue;

      // Cutoff check
      if (lastOrderDays && lastOrderDays.length > 0) {
        // Closest last-order day that is on or before this bake day in the same week
        const eligible = lastOrderDays.filter(ld => ld <= ourIdx).sort((a, b) => b - a);
        if (eligible.length > 0) {
          const cutoffDayIdx = eligible[0];
          const cutoffDate = new Date(d);
          cutoffDate.setDate(cutoffDate.getDate() - (ourIdx - cutoffDayIdx));
          if (lastOrderTime) {
            const [h, m, s] = lastOrderTime.split(':').map(Number);
            cutoffDate.setHours(h, m, s || 0, 0);
          } else {
            cutoffDate.setHours(23, 59, 59, 0);
          }
          if (now > cutoffDate) continue; // cutoff has passed for this bake date
        }
      }

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="text-center">
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🍞</div>
        <Spinner animation="border" style={{ color: '#059669' }} />
      </div>
    </div>
  );

  if (error && !store) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="text-center">
        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🥖</div>
        <h3 style={{ color: '#94a3b8', fontWeight: 700 }}>{error}</h3>
        <p style={{ color: '#cbd5e1' }}>Please check the URL and try again.</p>
      </div>
    </div>
  );

  // store_name wins; fall back to company_name injected by backend, then subdomain
  const storeName = store?.store_name || store?.company_name || resolvedSubdomain || 'Our Bakery';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: T.bg, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* Sticky Navbar */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, background: T.primaryDark,
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
      }}>
        {/* Main bar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', height: 56 }}>
          {/* Brand name / inline logo when hero is hidden */}
          {store?.navbar_show_brand !== false && (
            <div style={{ flex: 1, fontWeight: 800, fontSize: '1rem', color: 'white', letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>
              <button onClick={() => { setActivePage(null); setMenuOpen(false); }} style={{ background: 'none', border: 'none', color: 'white', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {store?.hide_hero && store?.logo && (
                  <img src={store.logo} alt={storeName} style={{ height: 36, width: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                )}
                {storeName}
              </button>
            </div>
          )}

          {/* Desktop: page links inline */}
          {!isMobile && pages.length > 0 && (() => {
            const align = store?.navbar_menu_align || 'left';
            const containerStyle: React.CSSProperties = {
              display: 'flex', alignItems: 'center', gap: 2,
              flex: align === 'center' ? 1 : (align === 'right' ? 1 : 0),
              justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start'),
            };
            if (store?.navbar_show_brand === false && align === 'left') containerStyle.flex = 1;
            return (
              <div style={containerStyle}>
                {pages.map(pg => (
                  <button key={pg.slug} onClick={() => setActivePage(pg.slug)}
                    style={{ background: activePage === pg.slug ? 'rgba(255,255,255,0.18)' : 'none', border: 'none', color: activePage === pg.slug ? 'white' : 'rgba(255,255,255,0.75)', fontSize: '0.82rem', cursor: 'pointer', padding: '5px 11px', borderRadius: '8px', fontWeight: activePage === pg.slug ? 700 : 400, whiteSpace: 'nowrap' }}>
                    {pg.title}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Auth + cart buttons (always visible) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            {!isMobile && (
              member ? (
                <>
                  <button onClick={loadMyOrders} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px' }}>
                    My Orders
                  </button>
                  <button onClick={openProfileModal} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px' }}>
                    My Profile
                  </button>
                  <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {member.full_name}
                  </span>
                  <button onClick={handleLogout} title="Log out" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '4px' }}>
                    <ArrowRightOnRectangleIcon style={{ width: 18, height: 18 }} />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setAuthModal('login')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', cursor: 'pointer', padding: '6px 12px', borderRadius: '8px' }}>
                    Log in
                  </button>
                  <button onClick={() => setAuthModal('register')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', fontSize: '0.8rem', cursor: 'pointer', padding: '6px 14px', borderRadius: '20px', fontWeight: 600 }}>
                    Sign up
                  </button>
                </>
              )
            )}

            <button
              onClick={() => setCartOpen(true)}
              style={{
                background: cartCount > 0 ? T.primary : 'rgba(255,255,255,0.1)',
                border: cartCount > 0 ? 'none' : '1px solid rgba(255,255,255,0.25)',
                color: 'white', cursor: 'pointer', padding: '7px 14px',
                borderRadius: '22px', marginLeft: 4, display: 'flex', alignItems: 'center', gap: 6,
                fontWeight: 700, fontSize: '0.85rem',
                transform: cartAnim ? 'scale(1.12)' : 'scale(1)',
                transition: 'all 0.2s',
              }}
            >
              <ShoppingCartIcon style={{ width: 18, height: 18 }} />
              {cartCount > 0 && <span>{cartCount}</span>}
            </button>

            {/* Hamburger toggle — mobile only */}
            {isMobile && (
              <button
                onClick={() => setMenuOpen(o => !o)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', marginLeft: 4 }}
                aria-label="Toggle menu"
              >
                {menuOpen
                  ? <XMarkIcon style={{ width: 24, height: 24 }} />
                  : <Bars3Icon style={{ width: 24, height: 24 }} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {isMobile && menuOpen && (
          <div style={{ background: T.primaryDark, borderTop: '1px solid rgba(255,255,255,0.12)', padding: '8px 16px 16px' }}>
            {/* Page links */}
            {pages.map(pg => (
              <button key={pg.slug}
                onClick={() => { setActivePage(pg.slug); setMenuOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: activePage === pg.slug ? 'rgba(255,255,255,0.15)' : 'none', border: 'none', color: 'white', fontSize: '0.95rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', fontWeight: activePage === pg.slug ? 700 : 400, marginBottom: 2 }}>
                {pg.title}
              </button>
            ))}
            {/* Auth actions */}
            <div style={{ borderTop: pages.length ? '1px solid rgba(255,255,255,0.12)' : 'none', marginTop: pages.length ? 8 : 0, paddingTop: pages.length ? 8 : 0 }}>
              {member ? (
                <>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', padding: '6px 12px' }}>{member.full_name}</div>
                  <button onClick={() => { loadMyOrders(); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px' }}>
                    My Orders
                  </button>
                  <button onClick={() => { openProfileModal(); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px' }}>
                    My Profile
                  </button>
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px' }}>
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => { setAuthModal('login'); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px' }}>
                    Log in
                  </button>
                  <button onClick={() => { setAuthModal('register'); setMenuOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '0.9rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', fontWeight: 600 }}>
                    Sign up
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Schedule bar — shown below navbar when hero is hidden */}
      {store?.hide_hero && (scheduleLabel(store?.bake_days) || scheduleLabel(store?.last_order_days) || (store?.bake_schedule_type === 'exact_dates' && (store?.bake_exact_dates || []).length > 0)) && (
        <div style={{ background: T.primaryDark, borderTop: '1px solid rgba(255,255,255,0.12)', padding: '10px 24px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'center' }}>
          {store?.bake_schedule_type === 'exact_dates' ? (
            (store?.bake_exact_dates || []).slice(0, 3).map((e: any) => (
              <span key={e.date} style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', fontWeight: 600 }}>
                🍞 {new Date(e.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                {e.cutoff_date && <span style={{ opacity: 0.65, fontWeight: 400 }}> · order by {new Date(e.cutoff_date + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{e.cutoff_time ? ' ' + e.cutoff_time : ''}</span>}
              </span>
            ))
          ) : (
            <>
              {scheduleLabel(store?.bake_days) && (
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', fontWeight: 600 }}>
                  🍞 Bakes: <strong>{scheduleLabel(store.bake_days)}</strong>
                </span>
              )}
              {scheduleLabel(store?.last_order_days) && (
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', fontWeight: 600 }}>
                  ⏰ Order by: <strong>{scheduleLabel(store.last_order_days)}</strong>
                  {store?.last_order_time && <span style={{ fontWeight: 400, opacity: 0.75 }}> · {store.last_order_time}</span>}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Hero — full-bleed with independently positioned logo and text */}
      {!store?.hide_hero && (
      <div style={{
        background: store?.hero_image
          ? `linear-gradient(rgba(0,0,0,0.48), rgba(0,0,0,0.52)), url(${store.hero_image}) center/cover no-repeat`
          : T.hero,
        minHeight: 380, position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative circles only when no photo */}
        {!store?.hero_image && <>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'absolute', bottom: -60, left: -30, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        </>}

        {/* Logo */}
        <div style={heroElStyle(store?.logo_position || 'middle-center')}>
          <div style={{
            width: (store?.logo_shape === 'none' && store?.logo) ? undefined : logoSize,
            height: (store?.logo_shape === 'none' && store?.logo) ? undefined : logoSize,
            maxWidth: store?.logo_shape === 'none' ? logoSize : undefined,
            maxHeight: store?.logo_shape === 'none' ? logoSize : undefined,
            borderRadius: store?.logo_shape === 'none' ? 0 : logoRadius,
            border: store?.logo_shape === 'none' ? 'none' : logoBorder,
            boxShadow: (store?.logo_shape !== 'none' && logoBorder !== 'none') ? '0 6px 24px rgba(0,0,0,0.28)' : 'none',
            overflow: 'hidden',
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {store?.logo
              ? <img src={store.logo} alt={storeName} style={{ width: store?.logo_shape === 'none' ? 'auto' : '100%', height: store?.logo_shape === 'none' ? 'auto' : '100%', maxWidth: logoSize, maxHeight: logoSize, objectFit: store?.logo_shape === 'none' ? 'contain' : 'cover' }} />
              : <span style={{ fontSize: logoSize * 0.42 + 'px' }}>🥖</span>
            }
          </div>
        </div>

        {/* Store name + tagline + CTA */}
        <div style={{ ...heroElStyle(store?.name_position || 'middle-center'), maxWidth: 560 }}>
          <h1 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 900, color: 'white', marginBottom: '8px', letterSpacing: '-0.5px', textShadow: '0 2px 8px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
            {storeName}
          </h1>
          {store?.tagline && (
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', marginBottom: '20px', textShadow: '0 1px 4px rgba(0,0,0,0.4)', lineHeight: 1.5 }}>
              {store.tagline}
            </p>
          )}
          {products.length > 0 && (
            <button
              onClick={() => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ background: 'white', color: T.primaryDark, border: 'none', borderRadius: '28px', padding: '11px 30px', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              Shop Now
            </button>
          )}
          {error && (
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '10px 18px', display: 'inline-block', marginTop: '12px', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Schedule badge */}
        {store?.show_schedule_in_hero && (scheduleLabel(store?.bake_days) || scheduleLabel(store?.last_order_days)) && (
          <div style={{ ...heroElStyle(store?.schedule_position || 'bottom-center'), zIndex: 3, pointerEvents: 'none' }}>
            <div style={{
              background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
              borderRadius: '14px', padding: '10px 16px',
              fontSize: SCHEDULE_FONT[store?.schedule_size || 'medium'],
              color: 'white', lineHeight: 1.6,
            }}>
              {scheduleLabel(store?.bake_days) && (
                <div><span style={{ opacity: 0.75 }}>🍞 Bakes: </span><strong>{scheduleLabel(store.bake_days)}</strong></div>
              )}
              {scheduleLabel(store?.last_order_days) && (
                <div>
                  <span style={{ opacity: 0.75 }}>⏰ Order by: </span>
                  <strong>{scheduleLabel(store.last_order_days)}</strong>
                  {store?.last_order_time && <span style={{ opacity: 0.75 }}> · {store.last_order_time}</span>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── Page Content View ──────────────────────────────── */}
      {activePage && (() => {
        const pg = pages.find(p => p.slug === activePage);
        if (!pg) return null;
        return (
          <Container className="py-5" style={{ maxWidth: 780 }}>
            <button onClick={() => setActivePage(null)} style={{ background: 'none', border: 'none', color: T.primary, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', marginBottom: 20, padding: 0 }}>
              ← Back to Store
            </button>
            <h2 style={{ fontWeight: 800, fontSize: '1.8rem', color: '#1e293b', marginBottom: 24 }}>{pg.title}</h2>
            <div
              style={{ color: '#374151', lineHeight: 1.8, fontSize: '1rem' }}
              dangerouslySetInnerHTML={{ __html: pg.content }}
            />
          </Container>
        );
      })()}

      {/* ── Products Section (hidden when viewing a page) ──── */}
      {!activePage && <>

      {/* Products */}
      <Container className="py-5" id="products-section" style={{ flex: 1 }}>
        {orderSuccess && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '16px 20px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircleIcon style={{ width: 28, height: 28, color: '#059669', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, color: '#064e3b' }}>
                {orderSuccess._stripe_paid ? 'Payment confirmed! 🎉' : 'Order placed successfully!'}
              </div>
              <div style={{ color: '#059669', fontSize: '0.85rem' }}>
                {orderSuccess.order_number
                  ? <>Order <strong>{orderSuccess.order_number}</strong> {orderSuccess._stripe_paid ? 'paid and confirmed.' : "received. We'll be in touch soon."}</>
                  : "We'll be in touch soon."}
              </div>
            </div>
            <button onClick={() => setOrderSuccess(null)} style={{ background: 'none', border: 'none', marginLeft: 'auto', cursor: 'pointer', color: '#94a3b8' }}>
              <XMarkIcon style={{ width: 18, height: 18 }} />
            </button>
          </div>
        )}

        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '28px' }}>
            {['all', ...categories].map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{ background: activeCategory === cat ? T.primary : 'white', color: activeCategory === cat ? 'white' : '#475569', border: `1px solid ${activeCategory === cat ? T.primary : '#e2e8f0'}`, borderRadius: '22px', padding: '6px 18px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                {cat === 'all' ? '✨ All Products' : cat}
              </button>
            ))}
          </div>
        )}

        {visibleProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🛒</div>
            <h4 style={{ fontWeight: 700 }}>No products available</h4>
            <p style={{ fontSize: '0.9rem' }}>Check back soon — fresh products are being added.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '20px' }}>
              <h2 style={{ fontWeight: 800, color: T.text, fontSize: '1.4rem' }}>
                {activeCategory === 'all' ? 'All Products' : activeCategory}
              </h2>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{visibleProducts.length} items</span>
            </div>
            <Row className="g-4">
              {visibleProducts.map(product => {
                const inCart = cart.find(c => c.product.id === product.id);
                const emoji = getProductEmoji(product.name, product.category?.name || '');
                return (
                  <Col key={product.id} xs={12} sm={6} lg={4} xl={3}>
                    <div style={{
                      background: 'white', borderRadius: '18px', overflow: 'hidden',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)',
                      border: inCart ? `2px solid ${T.primary}` : '2px solid transparent',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      height: '100%', display: 'flex', flexDirection: 'column',
                    }}>
                      <div style={{ height: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', background: product.image ? 'transparent' : `linear-gradient(135deg, ${T.bg} 0%, ${T.primary}18 100%)`, position: 'relative', overflow: 'hidden' }}>
                        {product.image
                          ? <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: '3.8rem' }}>{emoji}</span>
                        }
                        {product.category?.name && (
                          <span style={{ position: 'absolute', top: 10, left: 10, background: T.primary, color: 'white', borderRadius: '12px', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {product.category.name}
                          </span>
                        )}
                        {product.stock_quantity === 0 && (
                          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: 'white', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.06em' }}>SOLD OUT</span>
                          </div>
                        )}
                        {inCart && (
                          <div style={{ position: 'absolute', top: 10, right: 10, background: T.primary, color: 'white', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>
                            {inCart.quantity}
                          </div>
                        )}
                      </div>

                      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <h6 style={{ fontWeight: 700, color: T.text, marginBottom: 4, fontSize: '0.95rem' }}>{product.name}</h6>
                        {product.description && (
                          <p style={{ color: '#94a3b8', fontSize: '0.78rem', marginBottom: 8, lineHeight: 1.4, flex: 1, display: '-webkit-box' as any, WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>
                            {product.description}
                          </p>
                        )}
                        {/* Allergens */}
                        {product.allergens && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {product.allergens.split(',').map(a => a.trim()).filter(Boolean).map(a => (
                                <span key={a} style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                  ⚠ {a}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Ingredients toggle */}
                        {product.ingredients && (
                          <details style={{ marginBottom: 6 }}>
                            <summary style={{ fontSize: '0.72rem', color: '#94a3b8', cursor: 'pointer', userSelect: 'none', listStyle: 'none' }}>
                              <span style={{ textDecoration: 'underline dotted' }}>Ingredients ▾</span>
                            </summary>
                            <p style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>{product.ingredients}</p>
                          </details>
                        )}
                        {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
                          <div style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: 700, marginBottom: 6 }}>🔥 Only {product.stock_quantity} left!</div>
                        )}
                        <div style={{ marginTop: 'auto' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10 }}>
                            <span style={{ fontWeight: 900, fontSize: '1.25rem', color: T.primary }}>${parseFloat(product.price_per_unit).toFixed(2)}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginLeft: 2 }}>/{product.unit}</span>
                          </div>
                          {inCart ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <button onClick={() => updateQty(product.id, -1)} style={{ width: 32, height: 32, border: `1px solid ${T.cardBorder}`, borderRadius: '50%', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <MinusIcon style={{ width: 14, height: 14, color: T.primary }} />
                              </button>
                              <span style={{ flex: 1, textAlign: 'center', fontWeight: 800, color: T.text, fontSize: '0.95rem' }}>{inCart.quantity}</span>
                              <button onClick={() => updateQty(product.id, 1)} style={{ width: 32, height: 32, border: `1px solid ${T.cardBorder}`, borderRadius: '50%', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PlusIcon style={{ width: 14, height: 14, color: T.primary }} />
                              </button>
                              <button onClick={() => removeFromCart(product.id)} style={{ width: 32, height: 32, border: '1px solid #fee2e2', borderRadius: '50%', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <XMarkIcon style={{ width: 14, height: 14, color: '#ef4444' }} />
                              </button>
                            </div>
                          ) : (
                            <button disabled={product.stock_quantity === 0} onClick={() => addToCart(product)}
                              style={{ width: '100%', background: product.stock_quantity === 0 ? '#f1f5f9' : T.primary, color: product.stock_quantity === 0 ? '#94a3b8' : 'white', border: 'none', borderRadius: '10px', padding: '9px 0', fontWeight: 700, fontSize: '0.85rem', cursor: product.stock_quantity === 0 ? 'not-allowed' : 'pointer' }}>
                              {product.stock_quantity === 0 ? 'Sold Out' : 'Add to Cart'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </Container>

      {/* Home Bakery Legal Statement */}
      {store?.show_home_bakery_statement && store?.home_bakery_statement && (
        <div style={{ background: '#fffbeb', borderTop: '1px solid #fde68a', padding: '14px 24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#92400e', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', lineHeight: 1.6 }}>
            ⚠ {store.home_bakery_statement}
          </p>
        </div>
      )}

      {/* Footer */}
      <footer style={{ background: T.primaryDark, color: 'rgba(255,255,255,0.8)', marginTop: 0 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 32px 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40 }}>
          {/* Brand */}
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', color: 'white', marginBottom: 10 }}>
              {store?.logo && store?.logo_shape !== 'circle' && (
                <img src={store.logo} alt={storeName} style={{ height: 36, objectFit: 'contain', marginRight: 10, verticalAlign: 'middle' }} />
              )}
              {storeName}
            </div>
            {store?.tagline && <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 12 }}>{store.tagline}</p>}
            {store?.footer_about && <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>{store.footer_about}</p>}
            {/* Social icons */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              {store?.instagram_url && (
                <a href={store.instagram_url} target="_blank" rel="noopener noreferrer" style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              )}
              {store?.facebook_url && (
                <a href={store.facebook_url} target="_blank" rel="noopener noreferrer" style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.532-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
                  </svg>
                </a>
              )}
              {store?.tiktok_url && (
                <a href={store.tiktok_url} target="_blank" rel="noopener noreferrer" style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Pages */}
          {pages.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Pages</div>
              {pages.map(pg => (
                <button key={pg.slug} onClick={() => { setActivePage(pg.slug); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  style={{ display: 'block', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0', textAlign: 'left' }}>
                  {pg.title}
                </button>
              ))}
            </div>
          )}

          {/* Contact */}
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Contact</div>
            {store?.contact_email && (
              <a href={`mailto:${store.contact_email}`} style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: 10, textDecoration: 'none' }}>
                ✉ {store.contact_email}
              </a>
            )}
            {store?.show_contact_form !== false && (
              <button onClick={() => setContactModal(true)}
                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '20px', padding: '8px 18px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', marginTop: 4 }}>
                Send a message
              </button>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, maxWidth: 1200, margin: '0 auto' }}>
          <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>© {new Date().getFullYear()} {storeName}</span>
          <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)' }}>Powered by Sourdoughsy</span>
        </div>
      </footer>

      </> /* end !activePage */}

      {/* Cart Sidebar */}
      {cartOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} onClick={() => setCartOpen(false)} />
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 'min(400px, 100vw)', background: 'white', boxShadow: '-8px 0 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 style={{ fontWeight: 800, margin: 0, color: '#1e293b' }}>Your Cart</h5>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
              </div>
              <button onClick={() => setCartOpen(false)} style={{ background: '#f8fafc', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XMarkIcon style={{ width: 18, height: 18, color: '#64748b' }} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', paddingTop: '60px', color: '#94a3b8' }}>
                  <ShoppingCartIcon style={{ width: 48, height: 48, color: '#e2e8f0', marginBottom: 12 }} />
                  <p>Your cart is empty</p>
                  <button onClick={() => setCartOpen(false)} style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '10px', padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>Browse Products</button>
                </div>
              ) : cart.map(item => (
                <div key={item.product.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '10px', background: `${T.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.4rem' }}>
                    {getProductEmoji(item.product.name, item.product.category?.name || '')}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>{item.product.name}</div>
                    <div style={{ color: T.primary, fontWeight: 700, fontSize: '0.85rem' }}>${(parseFloat(item.product.price_per_unit) * item.quantity).toFixed(2)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <button onClick={() => updateQty(item.product.id, -1)} style={{ width: 24, height: 24, border: '1px solid #e2e8f0', borderRadius: '50%', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MinusIcon style={{ width: 10, height: 10 }} />
                      </button>
                      <span style={{ width: 22, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem' }}>{item.quantity}</span>
                      <button onClick={() => updateQty(item.product.id, 1)} style={{ width: 24, height: 24, border: '1px solid #e2e8f0', borderRadius: '50%', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PlusIcon style={{ width: 10, height: 10 }} />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.product.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0', padding: '4px', alignSelf: 'flex-start' }}>
                    <XMarkIcon style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div style={{ padding: '20px 24px', borderTop: '1px solid #f1f5f9' }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                    <span>Subtotal</span><span>${cartSubtotal.toFixed(2)}</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#059669', marginBottom: 4, fontWeight: 600 }}>
                      <span>Coupon ({couponApplied?.code})</span><span>−${couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {taxAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                      <span>{store?.tax_label || 'Tax'} ({parseFloat(store?.tax_rate || 0)}%)</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {platformFeeCustomer > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                      <span>Service fee</span><span>${platformFeeCustomer.toFixed(2)}</span>
                    </div>
                  )}
                  {ccFeeCustomer > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                      <span>Processing fee</span><span>${ccFeeCustomer.toFixed(2)}</span>
                    </div>
                  )}
                  {deliveryFeeApplied > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                      <span>Delivery fee</span><span>${deliveryFeeApplied.toFixed(2)}</span>
                    </div>
                  )}
                  {isPickupOnly && (
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600, marginBottom: 4 }}>Pickup only — no delivery available</div>
                  )}
                  {isBothMode && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>
                      {fulfillmentChoice === 'pickup' ? '🛍 Pickup' : '🚚 Delivery'}
                    </div>
                  )}
                  {store?.tip_enabled && checkoutForm.tip_amount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 4 }}>
                      <span>Tip</span><span>${checkoutForm.tip_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 8, marginTop: 4 }}>
                    <span style={{ fontWeight: 700, color: '#1e293b' }}>Total</span>
                    <span style={{ fontWeight: 900, color: T.primary, fontSize: '1.2rem' }}>${cartTotal.toFixed(2)}</span>
                  </div>
                  {store?.tip_enabled && checkoutForm.tip_amount === 0 && (
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'right', marginTop: 2 }}>Tip added at checkout</div>
                  )}
                </div>
                <button onClick={() => { setCartOpen(false); if (!member) setAuthModal('login'); else openCheckout(); }}
                  style={{ width: '100%', background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '14px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer' }}>
                  {member ? 'Proceed to Checkout' : 'Log In to Checkout'}
                </button>
                {!member && (
                  <button onClick={() => { setCartOpen(false); openCheckout(); }}
                    style={{ width: '100%', background: 'none', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 0', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', color: '#64748b', marginTop: 8 }}>
                    Continue as Guest
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {store?.login_style === 'page' && !!authModal ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: T.hero, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '36px', maxWidth: 420, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', position: 'relative' }}>
            <button onClick={() => setAuthModal(null)} style={{ position: 'absolute', top: 16, right: 16, background: '#f8fafc', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: '1.2rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#1e293b' }}>{authModal === 'login' ? 'Welcome back 👋' : 'Create an account'}</div>
              <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '4px 0 0' }}>{authModal === 'login' ? 'Log in to place and track orders.' : 'Sign up to order from ' + storeName + '.'}</p>
            </div>
            {authError && <Alert variant={authError.includes('created') ? 'success' : 'danger'} style={{ fontSize: '0.82rem', borderRadius: '10px' }}>{authError}</Alert>}
            {authModal === 'register' && (
              <Row className="g-2 mb-2">
                <Col xs={6}><Form.Control placeholder="First name *" value={authForm.first_name} onChange={e => setAuthForm(p => ({ ...p, first_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
                <Col xs={6}><Form.Control placeholder="Last name" value={authForm.last_name} onChange={e => setAuthForm(p => ({ ...p, last_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
              </Row>
            )}
            <Form.Control className="mb-2" type="email" placeholder="Email address *" value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
            {authModal === 'register' && (
              <Form.Control className="mb-2" type="tel" placeholder="Phone number *" value={authForm.phone} onChange={e => setAuthForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
            )}
            <Form.Control className="mb-4" type="password" placeholder="Password *" value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAuth()} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
            <button className="w-100" onClick={handleAuth} disabled={authLoading}
              style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '13px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', opacity: authLoading ? 0.7 : 1, width: '100%' }}>
              {authLoading ? <Spinner size="sm" animation="border" /> : authModal === 'login' ? 'Log In' : 'Create Account'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: '#64748b' }}>
              {authModal === 'login' ? "Don't have an account? " : 'Already registered? '}
              <button style={{ background: 'none', border: 'none', color: T.primary, fontWeight: 700, cursor: 'pointer' }}
                onClick={() => { setAuthError(''); setAuthModal(authModal === 'login' ? 'register' : 'login'); }}>
                {authModal === 'login' ? 'Sign up' : 'Log in'}
              </button>
            </p>
          </div>
        </div>
      ) : (
      <Modal show={!!authModal} onHide={() => setAuthModal(null)} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', padding: '24px 24px 8px' }}>
          <div>
            <Modal.Title style={{ fontWeight: 800, fontSize: '1.3rem' }}>
              {authModal === 'login' ? 'Welcome back 👋' : 'Create an account'}
            </Modal.Title>
            <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>
              {authModal === 'login' ? 'Log in to place and track orders.' : 'Sign up to order from ' + storeName + '.'}
            </p>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px 24px 24px' }}>
          {authError && <Alert variant={authError.includes('created') ? 'success' : 'danger'} style={{ fontSize: '0.82rem', borderRadius: '10px' }}>{authError}</Alert>}
          {authModal === 'register' && (
            <Row className="g-2 mb-2">
              <Col xs={6}><Form.Control placeholder="First name *" value={authForm.first_name} onChange={e => setAuthForm(p => ({ ...p, first_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
              <Col xs={6}><Form.Control placeholder="Last name" value={authForm.last_name} onChange={e => setAuthForm(p => ({ ...p, last_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
            </Row>
          )}
          <Form.Control className="mb-2" type="email" placeholder="Email address *" value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
          {authModal === 'register' && (
            <Form.Control className="mb-2" type="tel" placeholder="Phone number *" value={authForm.phone} onChange={e => setAuthForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
          )}
          <Form.Control className="mb-4" type="password" placeholder="Password *" value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAuth()} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
          <button className="w-100" onClick={handleAuth} disabled={authLoading}
            style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '13px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', opacity: authLoading ? 0.7 : 1 }}>
            {authLoading ? <Spinner size="sm" animation="border" /> : authModal === 'login' ? 'Log In' : 'Create Account'}
          </button>
          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.85rem', color: '#64748b' }}>
            {authModal === 'login' ? "Don't have an account? " : 'Already registered? '}
            <button style={{ background: 'none', border: 'none', color: T.primary, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => { setAuthError(''); setAuthModal(authModal === 'login' ? 'register' : 'login'); }}>
              {authModal === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </Modal.Body>
      </Modal>
      )}

      {/* Checkout Modal */}
      <Modal show={checkoutModal} onHide={() => { setCheckoutModal(false); setCheckoutError(''); }} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', padding: '24px 24px 8px' }}>
          <Modal.Title style={{ fontWeight: 800 }}>Complete Your Order</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 24px 24px' }}>
          {!member && (
            <>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 8 }}>Guest checkout — we'll send updates to your email.</p>
              <Row className="g-2 mb-2">
                <Col xs={12}><Form.Control size="sm" type="email" placeholder="Your email *" value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} style={{ borderRadius: '8px' }} /></Col>
                <Col xs={6}><Form.Control size="sm" placeholder="First name" value={authForm.first_name} onChange={e => setAuthForm(p => ({ ...p, first_name: e.target.value }))} style={{ borderRadius: '8px' }} /></Col>
                <Col xs={6}><Form.Control size="sm" placeholder="Last name" value={authForm.last_name} onChange={e => setAuthForm(p => ({ ...p, last_name: e.target.value }))} style={{ borderRadius: '8px' }} /></Col>
              </Row>
              <Form.Control size="sm" className="mb-2" type="tel" placeholder="Phone number *" value={authForm.phone} onChange={e => setAuthForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '8px' }} />
            </>
          )}
          {member && (
            <Form.Control size="sm" className="mb-2" type="tel" placeholder="Phone number" value={checkoutForm.phone} onChange={e => setCheckoutForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '8px' }} />
          )}
          {store?.bake_schedule_type === 'exact_dates' ? (() => {
            const now = new Date();
            const available = (store?.bake_exact_dates || []).filter((e: any) => {
              if (!e.date) return false;
              const cutoff = e.cutoff_date && e.cutoff_time
                ? new Date(`${e.cutoff_date}T${e.cutoff_time}`)
                : e.cutoff_date ? new Date(`${e.cutoff_date}T23:59`) : null;
              return !cutoff || cutoff > now;
            });
            return (
              <Form.Select className="mb-2" size="sm" value={checkoutForm.bake_date} onChange={e => setCheckoutForm(p => ({ ...p, bake_date: e.target.value }))} style={{ borderRadius: '8px' }}>
                <option value="">Select bake date *</option>
                {available.map((e: any) => (
                  <option key={e.date} value={e.date}>
                    {new Date(e.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {e.cutoff_date ? ` (order by ${new Date(e.cutoff_date + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${e.cutoff_time ? ' ' + e.cutoff_time : ''})` : ''}
                  </option>
                ))}
              </Form.Select>
            );
          })() : (() => {
            const validDates = getValidBakeDates(
              store?.bake_days || [],
              store?.last_order_days || [],
              store?.last_order_time || '',
            );
            if (validDates.length > 0) {
              return (
                <Form.Select className="mb-2" size="sm" value={checkoutForm.bake_date} onChange={e => setCheckoutForm(p => ({ ...p, bake_date: e.target.value }))} style={{ borderRadius: '8px' }}>
                  <option value="">Select bake date *</option>
                  {validDates.map(dateStr => (
                    <option key={dateStr} value={dateStr}>
                      {new Date(dateStr + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}
                    </option>
                  ))}
                </Form.Select>
              );
            }
            // Fallback: free-form date picker if no bake days configured
            return (
              <Form.Control className="mb-2" size="sm" type="date" value={checkoutForm.bake_date} onChange={e => setCheckoutForm(p => ({ ...p, bake_date: e.target.value }))} style={{ borderRadius: '8px' }} />
            );
          })()}
          {isPickupOnly ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', fontSize: '0.82rem', color: '#15803d', fontWeight: 600, marginBottom: 8 }}>
              Pickup only — no delivery address needed
            </div>
          ) : isBothMode ? (
            <>
              {/* Delivery or Pickup toggle */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {(['delivery', 'pickup'] as const).map(opt => (
                  <button key={opt} onClick={() => {
                    setFulfillmentChoice(opt);
                    if (opt === 'pickup') setCheckoutForm(p => ({ ...p, delivery_address: '' }));
                  }}
                    style={{ flex: 1, padding: '8px 0', borderRadius: '8px', border: `2px solid ${fulfillmentChoice === opt ? (opt === 'pickup' ? '#059669' : '#3b82f6') : '#e5e7eb'}`, background: fulfillmentChoice === opt ? (opt === 'pickup' ? '#f0fdf4' : '#eff6ff') : 'white', color: fulfillmentChoice === opt ? (opt === 'pickup' ? '#15803d' : '#1d4ed8') : '#64748b', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                    {opt === 'delivery' ? '🚚 Delivery' : '🛍 Pickup'}
                  </button>
                ))}
              </div>
              {fulfillmentChoice === 'delivery' && (
                <Form.Control className="mb-2" as="textarea" rows={2} size="sm"
                  placeholder="Delivery address *"
                  value={checkoutForm.delivery_address} onChange={e => setCheckoutForm(p => ({ ...p, delivery_address: e.target.value }))} style={{ borderRadius: '8px' }} />
              )}
              {fulfillmentChoice === 'pickup' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', fontSize: '0.82rem', color: '#15803d', fontWeight: 600, marginBottom: 8 }}>
                  Pickup — no delivery address needed
                </div>
              )}
            </>
          ) : (
            <Form.Control className="mb-2" as="textarea" rows={2} size="sm"
              placeholder="Delivery address *"
              value={checkoutForm.delivery_address} onChange={e => setCheckoutForm(p => ({ ...p, delivery_address: e.target.value }))} style={{ borderRadius: '8px' }} />
          )}
          <Form.Control className="mb-3" as="textarea" rows={2} size="sm" placeholder="Order notes (optional)" value={checkoutForm.notes} onChange={e => setCheckoutForm(p => ({ ...p, notes: e.target.value }))} style={{ borderRadius: '8px' }} />
          {/* Coupon code */}
          <div style={{ marginBottom: 16 }}>
            {couponApplied ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '10px 14px' }}>
                <div>
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#059669', fontSize: '0.9rem' }}>{couponApplied.code}</span>
                  <span style={{ fontSize: '0.78rem', color: '#059669', marginLeft: 8 }}>
                    − {couponApplied.discount_type === 'percentage' ? `${parseFloat(couponApplied.discount_value)}%` : `$${parseFloat(couponApplied.discount_value).toFixed(2)}`} off
                    &nbsp;(−${parseFloat(couponApplied.discount_amount).toFixed(2)})
                  </span>
                </div>
                <button onClick={removeCoupon} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Form.Control size="sm" placeholder="Coupon code" value={couponInput}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                    style={{ borderRadius: '8px', fontFamily: 'monospace', fontWeight: 600 }} />
                  <button onClick={handleApplyCoupon} disabled={couponLoading || !couponInput.trim()}
                    style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '8px', padding: '0 16px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0, opacity: couponLoading || !couponInput.trim() ? 0.6 : 1 }}>
                    Apply
                  </button>
                </div>
                {couponError && <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>{couponError}</div>}
              </div>
            )}
          </div>
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px 16px', marginBottom: store?.tip_enabled ? 0 : 16 }}>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Order Summary</div>
            {cart.map(c => (
              <div key={c.product.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                <span style={{ color: '#475569' }}>{c.quantity}× {c.product.name}</span>
                <span style={{ fontWeight: 600 }}>${(parseFloat(c.product.price_per_unit) * c.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
              {couponDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#059669', marginBottom: 3, fontWeight: 600 }}>
                  <span>Discount ({couponApplied?.code})</span>
                  <span>−${couponDiscount.toFixed(2)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
                  <span>{store?.tax_label || 'Tax'} ({parseFloat(store?.tax_rate || 0)}%)</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
              )}
              {platformFeeCustomer > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
                  <span>Service fee</span><span>${platformFeeCustomer.toFixed(2)}</span>
                </div>
              )}
              {ccFeeCustomer > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
                  <span>Processing fee</span><span>${ccFeeCustomer.toFixed(2)}</span>
                </div>
              )}
              {deliveryFeeApplied > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
                  <span>Delivery fee</span><span>${deliveryFeeApplied.toFixed(2)}</span>
                </div>
              )}
              {checkoutForm.tip_amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
                  <span>Tip</span><span>${checkoutForm.tip_amount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6 }}>
                <span style={{ fontWeight: 700 }}>Total</span>
                <span style={{ fontWeight: 900, color: T.primary, fontSize: '1.05rem' }}>${cartTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          {/* Tip selector */}
          {store?.tip_enabled && (() => {
            const presets = store?.tip_percentages?.length ? store.tip_percentages : [10, 15, 20];
            return (
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px 16px', marginBottom: 16, marginTop: 8 }}>
                <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Add a Tip <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8', letterSpacing: 0 }}>(optional)</span></div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => { setTipPct(0); setTipCustom(''); setCheckoutForm(p => ({ ...p, tip_amount: 0 })); }}
                    style={{ padding: '6px 12px', borderRadius: '20px', border: `2px solid ${tipPct === 0 ? T.primary : '#e2e8f0'}`, background: tipPct === 0 ? T.primary : 'white', color: tipPct === 0 ? 'white' : '#374151', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                    No tip
                  </button>
                  {presets.map((pct: number) => (
                    <button key={pct} onClick={() => { setTipPct(pct); setTipCustom(''); setCheckoutForm(p => ({ ...p, tip_amount: parseFloat((cartSubtotal * pct / 100).toFixed(2)) })); }}
                      style={{ padding: '6px 12px', borderRadius: '20px', border: `2px solid ${tipPct === pct ? T.primary : '#e2e8f0'}`, background: tipPct === pct ? T.primary : 'white', color: tipPct === pct ? 'white' : '#374151', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                      {pct}% <span style={{ opacity: 0.7, fontSize: '0.72rem' }}>(${(cartSubtotal * pct / 100).toFixed(2)})</span>
                    </button>
                  ))}
                  <button onClick={() => { setTipPct('custom'); }}
                    style={{ padding: '6px 12px', borderRadius: '20px', border: `2px solid ${tipPct === 'custom' ? T.primary : '#e2e8f0'}`, background: tipPct === 'custom' ? T.primary : 'white', color: tipPct === 'custom' ? 'white' : '#374151', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                    Custom
                  </button>
                </div>
                {tipPct === 'custom' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <span style={{ color: '#374151', fontWeight: 700 }}>$</span>
                    <Form.Control type="number" size="sm" min={0} step={0.50} placeholder="0.00" value={tipCustom}
                      onChange={e => { setTipCustom(e.target.value); setCheckoutForm(p => ({ ...p, tip_amount: parseFloat(e.target.value) || 0 })); }}
                      style={{ borderRadius: '8px', maxWidth: 120 }} />
                  </div>
                )}
              </div>
            );
          })()}
          <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 14px', fontSize: '0.8rem', color: '#92400e', marginBottom: 16 }}>
            {store?.accepts_online_payment
              ? '💳 You will be redirected to Stripe to complete payment securely.'
              : '📋 Payment is arranged separately with the bakery after your order.'}
          </div>
          {checkoutError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '10px 14px', fontSize: '0.82rem', color: '#dc2626', fontWeight: 600, marginBottom: 12 }}>
              {checkoutError}
            </div>
          )}
          <button className="w-100" onClick={handlePlaceOrder} disabled={checkoutLoading}
            style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '14px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', opacity: checkoutLoading ? 0.7 : 1 }}>
            {checkoutLoading
              ? <Spinner size="sm" animation="border" />
              : store?.accepts_online_payment ? '💳 Proceed to Payment' : 'Place Order'}
          </button>
        </Modal.Body>
      </Modal>

      {/* My Orders Modal */}
      <Modal show={ordersModal} onHide={() => setOrdersModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9', padding: '20px 24px' }}>
          <Modal.Title style={{ fontWeight: 800 }}>My Orders</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
          {myOrders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
              <TruckIcon style={{ width: 48, height: 48, color: '#e2e8f0', marginBottom: 12 }} />
              <p>No orders yet.</p>
            </div>
          ) : myOrders.map(o => (
            <div key={o.id} style={{ padding: '18px 24px', borderBottom: '1px solid #f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, color: T.primary }}>{o.order_number}</div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ClockIcon style={{ width: 12, height: 12 }} />
                    {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ display: 'inline-block', background: (STATUS_COLOR[o.status] || '#94a3b8') + '20', color: STATUS_COLOR[o.status] || '#94a3b8', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', textTransform: 'capitalize' }}>
                    {o.status}
                  </span>
                  <div style={{ fontWeight: 800, color: '#1e293b' }}>${parseFloat(o.total_amount || 0).toFixed(2)}</div>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#64748b' }}>
                {o.items?.map((i: any) => `${i.quantity}× ${i.product_name}`).join(' · ')}
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {/* My Profile Modal */}
      <Modal show={profileModal} onHide={() => { setProfileModal(false); setProfileError(''); }} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', padding: '24px 24px 8px' }}>
          <div>
            <Modal.Title style={{ fontWeight: 800, fontSize: '1.15rem' }}>My Profile</Modal.Title>
            <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Update your saved details.</p>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 24px 24px' }}>
          {profileError && <Alert variant="danger" style={{ fontSize: '0.82rem', borderRadius: '10px' }}>{profileError}</Alert>}
          <Row className="g-2 mb-2">
            <Col xs={6}><Form.Control placeholder="First name" value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
            <Col xs={6}><Form.Control placeholder="Last name" value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
          </Row>
          <Form.Control className="mb-2" type="email" value={member?.email || ''} readOnly style={{ borderRadius: '10px', fontSize: '0.9rem', background: '#f8fafc', color: '#94a3b8' }} />
          <Form.Control className="mb-2" placeholder="Phone number *" type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
          <Form.Control className="mb-3" as="textarea" rows={2} placeholder="Default delivery address (optional — pre-fills at checkout)" value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} />
          <button className="w-100" onClick={handleSaveProfile} disabled={profileSaving}
            style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '13px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', opacity: profileSaving ? 0.7 : 1 }}>
            {profileSaving ? <Spinner size="sm" animation="border" /> : 'Save Changes'}
          </button>
        </Modal.Body>
      </Modal>

      {/* Contact Form Modal */}
      <Modal show={contactModal} onHide={() => { setContactModal(false); setContactDone(false); setContactError(''); }} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', padding: '22px 24px 8px' }}>
          <div>
            <Modal.Title style={{ fontWeight: 800, fontSize: '1.15rem' }}>Get in Touch</Modal.Title>
            <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>We'd love to hear from you.</p>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 24px 24px' }}>
          {contactDone ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 700, color: '#059669', fontSize: '1.05rem', marginBottom: 6 }}>Message sent!</div>
              <p style={{ color: '#64748b', fontSize: '0.88rem' }}>We'll get back to you as soon as possible.</p>
              <button onClick={() => { setContactModal(false); setContactDone(false); }} style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '10px', padding: '10px 24px', fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>Close</button>
            </div>
          ) : (
            <>
              {contactError && <Alert variant="danger" style={{ fontSize: '0.82rem', borderRadius: '10px' }}>{contactError}</Alert>}
              <Row className="g-2 mb-2">
                <Col xs={6}><Form.Control placeholder="Your name *" value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
                <Col xs={6}><Form.Control type="email" placeholder="Email address *" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
                <Col xs={6}><Form.Control placeholder="Phone (optional)" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
                <Col xs={6}><Form.Control placeholder="Subject (optional)" value={contactForm.subject} onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
                <Col xs={12}><Form.Control as="textarea" rows={4} placeholder="Your message *" value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} style={{ borderRadius: '10px', fontSize: '0.9rem' }} /></Col>
              </Row>
              <button className="w-100" onClick={async () => {
                setContactError('');
                if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
                  setContactError('Name, email and message are required.'); return;
                }
                setContactSending(true);
                try {
                  await api.submitContact(contactForm);
                  setContactDone(true);
                  setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
                } catch (e: any) {
                  setContactError(e?.response?.data?.error || 'Failed to send. Please try again.');
                }
                setContactSending(false);
              }} disabled={contactSending}
                style={{ background: T.primary, color: 'white', border: 'none', borderRadius: '12px', padding: '13px 0', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', opacity: contactSending ? 0.7 : 1, width: '100%' }}>
                {contactSending ? <Spinner size="sm" animation="border" /> : 'Send Message'}
              </button>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
